/**
 * GmEventAnnouncement — top-center banner showing the active event's
 * title, phase, countdown, and participant count.
 *
 * Subscribes to `gmEventAnnouncement` / `gmEventPhaseChanged` broadcasts
 * via the useGmEvent hook. Auto-fades after the announcement timeout.
 *
 * Mounted as a HUD overlay (not a window) so it's always visible during
 * an active event.
 *
 * @see docs/design/GM_EVENT_INTEGRATION_SPEC.md §3.17
 */

import React from 'react';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS } from '../../theme';

// FONTS shape: { ui, mono, display } — use FONTS.ui for body text.

const PHASE_LABELS: Record<string, string> = {
  pending: 'Pending',
  announced: '📢 Announced',
  registration: '📝 Registration Open',
  staging: '⚔️ Staging',
  live: '🔴 LIVE',
  finished: '🏆 Finished',
  cancelled: '❌ Cancelled',
};

export const GmEventAnnouncementBar: React.FC = () => {
  const { announcement, cancelled } = useGmEvent();

  // A cancel NULLS the announcement, so before this branch the banner just
  // vanished with no explanation — the staging gate's "Not enough
  // participants" cancel looked like the UI silently breaking (PTR find
  // 2026-07-10). Red variant, auto-clears with the service's 15s window.
  if (!announcement && cancelled) {
    return (
      <div
        data-testid="gm-event-cancelled-banner"
        style={{
          position: 'fixed',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          background: COLORS.surface,
          border: '1px solid rgba(231, 76, 60, 0.7)',
          borderRadius: 8,
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontFamily: FONTS.ui,
          fontSize: 14,
          zIndex: 9000,
          pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}
      >
        <span style={{ fontWeight: 'bold', color: '#e74c3c' }}>❌ Event cancelled</span>
        {cancelled.reason && <span style={{ color: COLORS.textSecondary }}>{cancelled.reason}</span>}
      </div>
    );
  }

  if (!announcement) return null;

  const phaseLabel = PHASE_LABELS[announcement.phase] ?? announcement.phase;
  const mins = Math.floor(announcement.secondsRemaining / 60);
  const secs = Math.floor(announcement.secondsRemaining % 60);
  const timeStr = announcement.secondsRemaining > 0
    ? `${mins}:${secs.toString().padStart(2, '0')}`
    : '';

  return (
    <div
      data-testid="gm-event-announcement"
      style={{
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: '8px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        fontFamily: FONTS.ui,
        color: COLORS.textPrimary,
        fontSize: 14,
        zIndex: 9000,
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      }}
    >
      <span style={{ fontWeight: 'bold', color: COLORS.textAccent }}>
        🎪 {announcement.title}
      </span>
      <span style={{ color: COLORS.textSecondary }}>{phaseLabel}</span>
      {timeStr && (
        <span style={{ color: COLORS.warning, fontWeight: 'bold' }}>⏱ {timeStr}</span>
      )}
      <span style={{ color: COLORS.textMuted }}>
        👥 {announcement.participantCount}/{announcement.maxParticipants}
      </span>
    </div>
  );
};

/** Window content version (for the window registry). */
export const GmEventAnnouncementContent: React.FC = () => {
  const { announcement, ended, cancelled } = useGmEvent();

  if (ended) {
    return (
      <div style={{ padding: 16, color: COLORS.textPrimary, fontFamily: FONTS.ui }}>
        <h3 style={{ margin: '0 0 8px' }}>🏆 Event Ended</h3>
        <p>Winners: {ended.winnerIds.join(', ') || 'None'}</p>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div style={{ padding: 16, color: COLORS.textPrimary, fontFamily: FONTS.ui }}>
        <h3 style={{ margin: '0 0 8px' }}>❌ Event Cancelled</h3>
        {cancelled.reason && <p>{cancelled.reason}</p>}
      </div>
    );
  }

  if (!announcement) {
    return (
      <div style={{ padding: 16, color: COLORS.textMuted, fontFamily: FONTS.ui }}>
        No active event announcements.
      </div>
    );
  }

  const phaseLabel = PHASE_LABELS[announcement.phase] ?? announcement.phase;

  return (
    <div style={{ padding: 16, color: COLORS.textPrimary, fontFamily: FONTS.ui }}>
      <h3 style={{ margin: '0 0 8px', color: COLORS.textAccent }}>🎪 {announcement.title}</h3>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span>Phase: <strong>{phaseLabel}</strong></span>
        {announcement.secondsRemaining > 0 && (
          <span>Time: <strong style={{ color: COLORS.warning }}>
            {Math.floor(announcement.secondsRemaining / 60)}:
            {Math.floor(announcement.secondsRemaining % 60).toString().padStart(2, '0')}
          </strong></span>
        )}
        <span>Players: <strong>{announcement.participantCount}/{announcement.maxParticipants}</strong></span>
      </div>
    </div>
  );
};
